import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TIMELINE } from "@/data/content";
import { Reveal, SectionHeading } from "./Reveal";

const ArrowBtn = ({ dir, disabled, onClick }) => {
  const Icon = dir === "prev" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      data-testid={`timeline-${dir}`}
      aria-label={dir === "prev" ? "Previous age" : "Next age"}
      disabled={disabled}
      onClick={onClick}
      className="flex h-12 w-12 items-center justify-center rounded-full bg-navy text-cream transition-[background-color,opacity,transform] duration-300 hover:-translate-y-0.5 disabled:opacity-30 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40"
    >
      <Icon className="h-5 w-5" />
    </button>
  );
};

const AgeTrack = ({ index, setIndex }) => (
  <div className="relative mt-10">
    <div aria-hidden="true" className="absolute left-5 right-5 top-5 h-1 rounded-full bg-navy/10">
      <motion.div className="h-full rounded-full bg-teal" animate={{ width: `${(index / (TIMELINE.length - 1)) * 100}%` }} transition={{ type: "spring", stiffness: 160, damping: 24 }} />
    </div>
    <div role="tablist" aria-label="Choose an age" className="relative flex justify-between">
      {TIMELINE.map((t, i) => (
        <button
          key={t.age}
          type="button"
          role="tab"
          aria-selected={i === index}
          aria-label={`Age ${t.age}`}
          data-testid={`timeline-age-${t.age}`}
          onClick={() => setIndex(i)}
          className={`flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-full font-heading text-sm font-black transition-[background-color,color,transform] duration-300 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-teal/40 ${i === index ? "scale-110 bg-teal text-white shadow-lg" : i < index ? "bg-teal/20 text-navy" : "bg-white text-navy ring-1 ring-navy/15 hover:bg-gold/30"}`}
        >
          {t.age}
        </button>
      ))}
    </div>
  </div>
);

export const Timeline = () => {
  const [index, setIndex] = useState(0);
  const item = TIMELINE[index];

  return (
    <section id="timeline" aria-labelledby="timeline-heading" className="relative bg-[#F4EFE4] px-5 py-24 sm:px-8 lg:py-36">
      <div className="mx-auto max-w-7xl">
        <SectionHeading id="timeline-heading" eyebrow="Watch them grow" title="From first scribble to first masterpiece." intro="Tap through one sample child's artwork, from age 3 to 10. Saved together, the little pieces tell a big story." />
        <Reveal delay={0.1}>
          <div data-testid="art-timeline" className="mt-14 grid items-center gap-10 lg:mt-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
            <div className="relative mx-auto aspect-square w-full max-w-[520px]">
              <AnimatePresence initial={false}>
                <motion.div
                  key={item.age}
                  initial={{ opacity: 0, rotate: -4, scale: 0.94 }}
                  animate={{ opacity: 1, rotate: index % 2 ? 1.5 : -1.5, scale: 1 }}
                  exit={{ opacity: 0, rotate: 4, scale: 1.02 }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 rounded-[2rem] bg-white p-3 shadow-[0_30px_70px_-30px_rgba(18,52,90,0.5)]"
                >
                  <img src={item.image} alt={`Sample artwork at age ${item.age}: ${item.title}`} loading="lazy" width="800" height="800" className="h-full w-full rounded-[1.5rem] object-cover" />
                  <span className="absolute right-6 top-6 rounded-full bg-navy/85 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-cream">Sample</span>
                </motion.div>
              </AnimatePresence>
            </div>
            <div>
              <div className="relative min-h-[220px]">
                <AnimatePresence initial={false}>
                  <motion.div
                    key={item.age}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12, position: "absolute" }}
                    transition={{ duration: 0.45 }}
                    className="inset-x-0 top-0"
                  >
                    <p className="font-heading text-7xl sm:text-8xl font-black leading-none text-teal" data-testid="timeline-current-age">{item.age}<span className="ml-2 align-top text-2xl text-navy/70">yrs</span></p>
                    <h3 className="mt-5 font-heading text-2xl sm:text-3xl font-extrabold text-navy">{item.title}</h3>
                    <p className="mt-3 max-w-md text-base md:text-lg leading-relaxed text-navy/80">{item.note}</p>
                  </motion.div>
                </AnimatePresence>
              </div>
              <AgeTrack index={index} setIndex={setIndex} />
              <div className="mt-8 flex items-center gap-3">
                <ArrowBtn dir="prev" disabled={index === 0} onClick={() => setIndex((i) => Math.max(0, i - 1))} />
                <ArrowBtn dir="next" disabled={index === TIMELINE.length - 1} onClick={() => setIndex((i) => Math.min(TIMELINE.length - 1, i + 1))} />
                <p className="ml-2 text-sm text-navy/75">Illustrative sample, not a real child's artwork.</p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
